import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from './SEO';
import Header from './Header';
import { getPostBySlug, getRecentPosts } from '../data/blogPosts';
import { trackAppStoreCtaClick } from '../utils/analytics';
import './BlogPost.css';

const RELATED_CHECKERS = {
  'pore-clogging-ingredients-list': {
    to: '/pore-clogging-ingredient-checker',
    label: 'Check a full label with the Pore Clogging Ingredient Checker',
  },
  'skincare-ingredients-to-avoid-for-acne': {
    to: '/pore-clogging-ingredient-checker',
    label: 'Screen a product with the Pore Clogging Ingredient Checker',
  },
  'comedogenic-ingredients-avoid': {
    to: '/comedogenic-ingredient-checker',
    label: 'Check a full INCI list with the Comedogenic Ingredient Checker',
  },
  'comedogenic-ingredients-checker': {
    to: '/comedogenic-ingredient-checker',
    label: 'Open the free Comedogenic Ingredient Checker',
  },
  'non-comedogenic-meaning': {
    to: '/comedogenic-ingredient-checker',
    label: 'Review a product with the Comedogenic Ingredient Checker',
  },
  'is-coconut-oil-comedogenic': {
    to: '/comedogenic-ingredient-checker',
    label: 'Check the rest of the formula with the Comedogenic Ingredient Checker',
  },
};

function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const recentPosts = getRecentPosts(5);
  const relatedChecker = RELATED_CHECKERS[slug];

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="blog-post-page">
        <Header />
        <div className="blog-container">
          <h1>Post Not Found</h1>
          <p>The blog post you're looking for doesn't exist.</p>
          <Link to="/blog">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "name": post.title,
    "headline": post.title,
    "description": post.description,
    "image": post.featuredImage ? `https://www.useacnesafe.com${post.featuredImage}` : undefined,
    "datePublished": post.date,
    "dateModified": post.updatedDate || post.date,
    "author": { "@type": "Organization", "name": post.author || "Acne Safe Editorial Team" },
    "reviewedBy": post.reviewedBy ? { "@type": "Organization", "name": post.reviewedBy } : undefined,
    "publisher": {
      "@type": "Organization",
      "name": "Acne Safe",
      "url": "https://www.useacnesafe.com"
    },
    "mainEntityOfPage": `https://www.useacnesafe.com/blog/${post.slug}`
  };

  return (
    <div className="blog-post-page">
      <SEO 
        title={post.title}
        description={post.description}
        keywords={post.keywords}
        url={`https://www.useacnesafe.com/blog/${post.slug}`}
        image={post.featuredImage}
        type="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Header />
      
      <div className="blog-container">
        <Link to="/blog" className="back-to-blog">← Back to Blog</Link>
        
        <div className="blog-content-wrapper">
          <article className="blog-post">
          {post.featuredImage && (
            <div className="blog-post-featured-image">
              <img 
                src={post.featuredImage} 
                alt={post.title}
                className="blog-featured-img"
                loading="eager"
              />
            </div>
          )}
          <header className="blog-post-header">
            <div className="blog-post-meta">
              <span className="blog-category">{post.category}</span>
              <span className="blog-date">
                {new Date(`${post.date}T12:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
            <h1 className="blog-post-title">{post.title}</h1>
            <div className="blog-editorial-meta">
              <span>By {post.author || 'Acne Safe Editorial Team'}</span>
              {post.reviewedBy && <span>Reviewed by {post.reviewedBy}</span>}
              {post.updatedDate && (
                <span>
                  Updated {new Date(`${post.updatedDate}T12:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              )}
            </div>
            {post.reviewNote && <p className="blog-review-note">{post.reviewNote}</p>}
          </header>

          <div 
            className="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {relatedChecker && (
            <aside className="blog-checker-link" aria-label="Related ingredient checker">
              <h2>Check the complete ingredient list</h2>
              <p>Ingredient-level information is only one part of a formula. Paste the product's current full label to review all possible matches.</p>
              <Link to={relatedChecker.to}>{relatedChecker.label}</Link>
            </aside>
          )}

          <div className="blog-post-cta">
            <h3>Check your products in seconds</h3>
            <p>Scan any ingredient label with your camera or paste a list — Acne Safe flags pore-clogging ingredients before you buy.</p>
            <a href="https://apps.apple.com/us/app/acne-safe-ingredient-scanner/id6747936538" target="_blank" rel="noopener noreferrer" className="cta-button-blog" onClick={() => trackAppStoreCtaClick('blog_post')}>
              <img src="/download.png" alt="Download on App Store" className="download-button-image" />
            </a>
          </div>
          </article>

          {/* Recent Posts Sidebar */}
          <aside className="blog-sidebar">
          <h3>Recent Posts</h3>
          <ul className="recent-posts-list">
            {recentPosts
              .filter(recentPost => recentPost.slug !== slug)
              .slice(0, 5)
              .map(recentPost => (
                <li key={recentPost.id}>
                  <Link to={`/blog/${recentPost.slug}`}>{recentPost.title}</Link>
                </li>
              ))}
          </ul>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default BlogPost;
